// insertConflictAssignments — VQE-Edit edit-actions for an INSERT structured
// tail's assignment list, tail-discriminated (§2.4 S6b, decision 8):
//   - add-insert-conflict-assignment      (append `, target = value`)
//   - modify-insert-conflict-assignment   (replace one assignment's value bytes)
//   - remove-insert-conflict-assignment   (drop the k-th assignment + separator)
//
// `tail` selects the anchor: 'on-conflict' targets `onConflict.updateAssignments`
// (PG/SQLite `ON CONFLICT … DO UPDATE SET …`), 'on-duplicate' targets
// `onDuplicate.assignments` (MySQL `ON DUPLICATE KEY UPDATE …`) — one wrapper
// family, two anchors, mirroring `modifyMergeBranch`'s selector pattern. These
// modules only validate inputs against an optional structure and delegate to
// `commitEdit` (validation throws BEFORE commit; the commit itself never rejects).
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.

import type { QueryStructure, UpdateAssignment } from '../../../../sql-resolver/types';
import type { ConflictTailTarget } from '../../../../sql-resolver/types/structuralEdit';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';

export type ConflictAssignmentOptions = {
  /** Optional structure used for tail-presence + index-bounds validation. */
  readonly structure?: QueryStructure;
};

function tailClauseLabel(tail: ConflictTailTarget): string {
  return tail === 'on-conflict' ? 'ON CONFLICT' : 'ON DUPLICATE KEY UPDATE';
}

/**
 * Resolve the tail's assignment list from a structure, or throw when the
 * requested tail's clause is absent (the wrapper-level twin of the
 * applicator's anchor-separation guard, which silently plans nothing).
 */
function requireTailAssignments(
  structure: QueryStructure,
  tail: ConflictTailTarget,
  label: string,
): readonly UpdateAssignment[] {
  const assigns =
    tail === 'on-conflict'
      ? structure.onConflict
        ? structure.onConflict.updateAssignments ?? []
        : null
      : structure.onDuplicate
        ? structure.onDuplicate.assignments
        : null;
  if (assigns === null) {
    throw new Error(`${label}: statement has no ${tailClauseLabel(tail)} clause`);
  }
  return assigns;
}

function assertIndexInRange(
  assigns: readonly UpdateAssignment[],
  index: number,
  label: string,
): void {
  if (assigns.length === 0) {
    throw new Error(`${label}: the assignment list is empty`);
  }
  if (index >= assigns.length) {
    throw new Error(
      `${label}: index ${index} out of range (assignment list has ${assigns.length} item(s))`,
    );
  }
}

export async function addInsertConflictAssignment(
  target: string,
  valueText: string,
  tail: ConflictTailTarget,
  ctx: CommitEditCtx,
  options: ConflictAssignmentOptions = {},
): Promise<CommitOutcome> {
  const t = target.trim();
  const v = valueText.trim();
  if (t.length === 0) {
    throw new Error('addInsertConflictAssignment: target must not be empty');
  }
  if (v.length === 0) {
    throw new Error('addInsertConflictAssignment: valueText must not be empty');
  }
  if (options.structure) {
    const assigns = requireTailAssignments(options.structure, tail, 'addInsertConflictAssignment');
    // An empty list (ON CONFLICT … DO NOTHING) has no anchor to append after —
    // the UI switches the action (with a seed) instead of appending.
    if (assigns.length === 0) {
      throw new Error(
        'addInsertConflictAssignment: the clause has no assignments to append after — switch the action to DO UPDATE first',
      );
    }
  }
  return commitEdit(
    { op: 'add-insert-conflict-assignment', target: t, valueText: v, tail },
    ctx,
  );
}

export async function modifyInsertConflictAssignment(
  index: number,
  newValueText: string,
  tail: ConflictTailTarget,
  ctx: CommitEditCtx,
  options: ConflictAssignmentOptions = {},
): Promise<CommitOutcome> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(
      `modifyInsertConflictAssignment: index must be a non-negative integer (got ${index})`,
    );
  }
  const v = newValueText.trim();
  if (v.length === 0) {
    throw new Error('modifyInsertConflictAssignment: newValueText must not be empty');
  }
  if (options.structure) {
    const assigns = requireTailAssignments(options.structure, tail, 'modifyInsertConflictAssignment');
    assertIndexInRange(assigns, index, 'modifyInsertConflictAssignment');
  }
  return commitEdit(
    { op: 'modify-insert-conflict-assignment', index, newValueText: v, tail },
    ctx,
  );
}

export async function removeInsertConflictAssignment(
  index: number,
  tail: ConflictTailTarget,
  ctx: CommitEditCtx,
  options: ConflictAssignmentOptions = {},
): Promise<CommitOutcome> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(
      `removeInsertConflictAssignment: index must be a non-negative integer (got ${index})`,
    );
  }
  if (options.structure) {
    const assigns = requireTailAssignments(options.structure, tail, 'removeInsertConflictAssignment');
    assertIndexInRange(assigns, index, 'removeInsertConflictAssignment');
    // Removing the only assignment would leave an ill-formed empty SET/UPDATE
    // list — the applicator refuses too (decision 5 defense in depth).
    if (assigns.length === 1) {
      throw new Error(
        'removeInsertConflictAssignment: at least one assignment must remain',
      );
    }
  }
  return commitEdit({ op: 'remove-insert-conflict-assignment', index, tail }, ctx);
}
